import { format } from 'date-fns';

/**
 * Generates and prints a professional callsheet
 * @param {Object} callsheet - The callsheet data
 * @returns {void}
 */
/**
 * Prints a callsheet with legal size paper as the default
 * @param {Object} callsheet - The callsheet data to print
 * @param {string} paperSize - The paper size to use (default: 'legal')
 * @param {string} margins - The margins to use (default: '0.5cm')
 * @param {boolean} adjustToFit - Whether to adjust content to fit the page (default: true)
 * @param {boolean} showMissingScheduleMessage - Whether to show a message if schedule data is missing (default: false)
 */
export const printCallsheet = (callsheet, paperSize = 'legal', margins = '0.5cm', adjustToFit = true, showMissingScheduleMessage = false) => {
  // Format date for display
  const formattedDate = callsheet.date ? format(new Date(callsheet.date), 'EEEE, MMMM d, yyyy') : 'Date not set';
  
  // Format general call time for display
  const formatTimeDisplay = (timeString) => {
    if (!timeString) return 'Not set';
    
    try {
      // Parse the time string (expected format: HH:MM)
      const [hours, minutes] = timeString.split(':').map(Number);
      
      // Create a date object and set the hours and minutes
      const date = new Date();
      date.setHours(hours);
      date.setMinutes(minutes);
      
      // Format the time with AM/PM
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }).toUpperCase();
    } catch (e) {
      console.error('Error formatting time:', e);
      return timeString; // Return the original string if parsing fails
    }
  };

  // Format pages to show ONLY the 1/8 fraction; hide 0. Whole pages appear as 8/8.
  const formatPagesEighthsOnly = (pages) => {
    if (pages === null || pages === undefined) return '';
    // Handle pre-formatted strings like "2 4/8" or "4/8" or "2"
    if (typeof pages === 'string') {
      const s = pages.trim();
      if (!s) return '';
      const matchWholeAndFrac = s.match(/^(\d+)\s+(\d)\/8$/); // e.g., "2 4/8"
      if (matchWholeAndFrac) {
        const frac = parseInt(matchWholeAndFrac[2], 10);
        return frac === 0 ? '8/8' : `${frac}/8`;
      }
      const matchFracOnly = s.match(/^(\d)\/8$/); // e.g., "4/8"
      if (matchFracOnly) {
        const frac = parseInt(matchFracOnly[1], 10);
        return frac === 0 ? '' : `${frac}/8`;
      }
      const matchWholeOnly = s.match(/^(\d+)$/); // e.g., "2"
      if (matchWholeOnly) {
        const whole = parseInt(matchWholeOnly[1], 10);
        return whole === 0 ? '' : '8/8';
      }
      // Fallback: try numeric parse
    }
    const num = typeof pages === 'number' ? pages : parseFloat(String(pages));
    if (isNaN(num) || num === 0) return '';
    const totalEighths = Math.round(num * 8);
    if (totalEighths <= 0) return '';
    const frac = totalEighths % 8;
    return frac === 0 ? '8/8' : `${frac}/8`;
  };

  // Choose a simple icon for the condition
  const getCondIcon = (cond) => {
    const c = (cond || '').toLowerCase();
    if (/(thunder|storm)/.test(c)) return '⛈️';
    if (/(rain|shower|drizzle)/.test(c)) return '🌧️';
    if (/(snow|sleet)/.test(c)) return '❄️';
    if (/(fog|haze)/.test(c)) return '🌫️';
    if (/(cloud)/.test(c)) return '☁️';
    if (/(wind)/.test(c)) return '🌬️';
    if (/(clear|sunny)/.test(c)) return '☀️';
    return '🌤️';
  };

  // Parse weather metrics from notes for graphic display
  const parseWeatherMetrics = (notes) => {
    if (!notes || typeof notes !== 'string') return {};
    try {
      const src = notes.replace(/\s+/g, ' ').trim();
      const tempMatch = src.match(/(\b-?\d{1,3})\s*°\s*([FC])/i);
      const tempVal = tempMatch ? tempMatch[1] : null;
      const tempUnit = tempMatch ? tempMatch[2].toUpperCase() : 'F';
      const condMatch = src.match(/\b(Sunny|Clear|Cloudy|Overcast|Rain|Showers|Thunderstorms|Storms|Snow|Sleet|Drizzle|Windy|Fog|Haze|Partly Cloudy|Mostly Cloudy)\b/i);
      const condition = condMatch ? condMatch[1] : null;
      const windMatch = src.match(/Wind[^:]*:\s*([\d.]+)\s*(mph|km\/h)/i) || src.match(/\b([\d.]+)\s*(mph|km\/h)\b/i);
      const windVal = windMatch ? windMatch[1] : null;
      const windUnit = windMatch ? windMatch[2] : null;
      const humMatch = src.match(/Humidity[^:]*:\s*(\d{1,3})%/i) || src.match(/\b(\d{1,3})%\b/);
      const humidity = humMatch ? humMatch[1] : null;
      return { tempVal, tempUnit, condition, windVal, windUnit, humidity };
    } catch {
      return {};
    }
  };

  // Prefer typed parkingLocations; fallback to first two lines of parkingNotes
  const extractParkingFromData = (cs) => {
    let crewParking = '';
    let basecamp = '';
    let productionOffice = '';
    try {
      let parking = Array.isArray(cs.parkingLocations) ? cs.parkingLocations : [];
      // Fallback: try localStorage if none present
      if ((!parking || parking.length === 0) && cs?.shootingDay) {
        const dayNum = parseInt(cs.shootingDay) || 1;
        // Try exact key with projectId if available
        try {
          if (cs?.projectId) {
            const key = `parkingLocations_${cs.projectId}_${dayNum}`;
            const raw = localStorage.getItem(key);
            const parsed = raw ? JSON.parse(raw) : [];
            if (Array.isArray(parsed) && parsed.length > 0) {
              parking = parsed;
            }
          }
        } catch {}
        // If still empty, scan localStorage for any matching day key
        try {
          if (!parking || parking.length === 0) {
            const suffix = `_${dayNum}`;
            for (let i = 0; i < localStorage.length; i++) {
              const k = localStorage.key(i);
              if (!k) continue;
              if (k.startsWith('parkingLocations_') && k.endsWith(suffix)) {
                const raw = localStorage.getItem(k);
                const parsed = raw ? JSON.parse(raw) : [];
                if (Array.isArray(parsed) && parsed.length > 0) {
                  parking = parsed;
                  break;
                }
              }
            }
          }
        } catch {}
      }
      if (Array.isArray(parking)) {
        const crewLoc = parking.find(p => (p?.type || '').toLowerCase() === 'crew');
        const baseLoc = parking.find(p => (p?.type || '').toLowerCase() === 'basecamp');
        const poLoc = parking.find(p => (p?.type || '').toLowerCase() === 'productionoffice');
        const fmt = (p) => {
          if (!p) return '';
          const name = (p.name || '').trim();
          const addr = (p.address || p?.placeData?.address || '').trim();
          if (name && addr) return `${name} — ${addr}`;
          return name || addr;
        };
        crewParking = fmt(crewLoc) || '';
        basecamp = fmt(baseLoc) || '';

        // If explicit crew/basecamp not provided, fallback to first two entries
        if ((!crewParking || !basecamp)) {
          const usable = parking.filter(p => fmt(p)).slice(0, 2);
          if (!crewParking && usable[0]) crewParking = fmt(usable[0]);
          if (!basecamp && usable[1]) basecamp = fmt(usable[1]);
        }
        if (poLoc) {
          const base = fmt(poLoc) || '';
          const phone = (poLoc.phone || '').trim();
          productionOffice = phone ? `${base}${base ? ' • ' : ''}Phone: ${phone}` : base;
        }
      }
      // Fallback to notes text: prefer top-level parkingNotes, else notes.parking
      const parkingText = (typeof cs.parkingNotes === 'string')
        ? cs.parkingNotes
        : (cs?.notes && typeof cs.notes.parking === 'string' ? cs.notes.parking : null);
      if ((!crewParking || !basecamp) && parkingText) {
        const lines = parkingText
          .split(/\r?\n|\u2028|\u2029/)
          .map(l => l.trim())
          .filter(Boolean);
        crewParking = crewParking || lines[0] || '';
        basecamp = basecamp || lines[1] || '';
      }
      // Prefer explicit basecamp fields if provided on callsheet
      const explicitBasecampName = (cs.basecampLocation || cs.basecampName || cs.basecamp || '').toString().trim();
      const explicitBasecampAddr = (cs.basecampAddress || '').toString().trim();
      if (explicitBasecampName || explicitBasecampAddr) {
        basecamp = explicitBasecampAddr && explicitBasecampName
          ? `${explicitBasecampName} — ${explicitBasecampAddr}`
          : (explicitBasecampName || explicitBasecampAddr);
      }
    } catch {}
    return { crewParking, basecamp, productionOffice };
  };
  const { crewParking, basecamp, productionOffice } = extractParkingFromData(callsheet);
  const nearestHospital = (
    (callsheet?.emergencyContacts?.hospital) ||
    (callsheet?.generalInfo?.nearestHospital) ||
    (callsheet?.nearestHospital) ||
    ''
  ).trim();
  
  // Get general call time from callsheet data
  const generalCallTime = callsheet.generalCallTime ? formatTimeDisplay(callsheet.generalCallTime) : 'Not set';
  const hasWorkingHours = (generalCallTime && generalCallTime !== 'Not set') || (callsheet.wrapTime && callsheet.wrapTime !== 'TBD');
  const hasParkingSummary = !!(callsheet.parkingNotes && callsheet.parkingNotes.trim());

  // Compact safety line: prefer top-level safetyNotes, fallback to notes.safety
  const safetyRaw = (
    (callsheet && callsheet.safetyNotes) ||
    (callsheet && callsheet.notes && callsheet.notes.safety) ||
    ''
  ).toString();
  const safetyLine = safetyRaw.replace(/\s+/g, ' ').trim();

  // Prefer accurate sunrise/sunset from server if provided; fallback to approximations by date
  const formatAnyTime = (t) => {
    if (!t) return '';
    try {
      const d = new Date(t);
      if (!isNaN(d.getTime())) {
        return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toUpperCase();
      }
    } catch {}
    // fallback if not a date
    return formatTimeDisplay(String(t));
  };
  // Try to parse Sunrise/Sunset directly from weatherNotes text if present
  const parseSunTimesFromWeatherNotes = (notes) => {
    if (!notes || typeof notes !== 'string') return { sunrise: '', sunset: '' };
    try {
      const src = notes.replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
      // Matches like "Sunrise: 6:31 AM" or "sunrise 6:31AM"
      const timeRe = /([0-1]?\d):([0-5]\d)\s*([AP]M)/i;
      const sunriseMatch = src.match(/sunrise[^0-9a-zA-Z]*([0-1]?\d:[0-5]\d\s*[AP]M)/i);
      const sunsetMatch = src.match(/sunset[^0-9a-zA-Z]*([0-1]?\d:[0-5]\d\s*[AP]M)/i);
      const sunrise = sunriseMatch && sunriseMatch[1] ? sunriseMatch[1].toUpperCase().replace(/\s*([AP]M)$/i, '\u00A0$1') : '';
      const sunset = sunsetMatch && sunsetMatch[1] ? sunsetMatch[1].toUpperCase().replace(/\s*([AP]M)$/i, '\u00A0$1') : '';
      return { sunrise, sunset };
    } catch {
      return { sunrise: '', sunset: '' };
    }
  };
  const { sunrise: parsedSunrise, sunset: parsedSunset } = parseSunTimesFromWeatherNotes(callsheet.weatherNotes || '');
  const sunriseStr = callsheet?.generalInfo?.sunriseTime
    ? formatAnyTime(callsheet.generalInfo.sunriseTime)
    : (parsedSunrise || getSunriseTime(callsheet.date));
  const sunsetStr = callsheet?.generalInfo?.sunsetTime
    ? formatAnyTime(callsheet.generalInfo.sunsetTime)
    : (parsedSunset || getSunsetTime(callsheet.date));

  // Weather metrics for card
  const wx = parseWeatherMetrics(callsheet.weatherNotes || '');
  const condIcon = getCondIcon(wx.condition);

  // Summarize weatherNotes into a concise printable line
  // Example outputs:
  //  - "80°F, Rain. Wind: 9 mph. Humidity: 73%."
  //  - "65°F, Cloudy. Wind: 5 mph. Humidity: 60%."
  const summarizeWeatherNotes = (notes) => {
    if (!notes || typeof notes !== 'string') return '';
    try {
      const src = notes.replace(/\s+/g, ' ').trim();

      // Extract temperature like 80°F or 22°C
      const tempMatch = src.match(/(\b-?\d{1,3}\s*°\s*[FC]\b)/i);
      const temp = tempMatch ? tempMatch[1].replace(/\s+/g, '') : null; // normalize like 80°F

      // Extract a primary condition keyword
      const condMatch = src.match(/\b(Sunny|Clear|Cloudy|Overcast|Rain|Showers|Thunderstorms|Storms|Snow|Sleet|Drizzle|Windy|Fog|Haze|Partly Cloudy|Mostly Cloudy)\b/i);
      const condition = condMatch ? condMatch[1] : null;

      // Extract wind as "Wind: 9 mph" (prefer explicit label), fallback to first speed mention with mph/km/h near word wind
      let wind = null;
      const windLabelMatch = src.match(/Wind[^:]*:\s*([\d.]+)\s*(mph|km\/h)/i);
      if (windLabelMatch) {
        wind = `Wind: ${windLabelMatch[1]} ${windLabelMatch[2]}`;
      } else {
        const windNearbyMatch = src.match(/Wind[^.]*?([\d.]+)\s*(mph|km\/h)/i);
        if (windNearbyMatch) {
          wind = `Wind: ${windNearbyMatch[1]} ${windNearbyMatch[2]}`;
        }
      }

      // Extract humidity
      const humMatch = src.match(/Humidity[^:]*:\s*(\d{1,3})%/i);
      const humidity = humMatch ? `Humidity: ${humMatch[1]}%` : null;

      // Build concise line in strict order
      const pieces = [];
      if (temp && condition) {
        pieces.push(`${temp}, ${condition}.`);
      } else if (temp) {
        pieces.push(`${temp}.`);
      } else if (condition) {
        pieces.push(`${condition}.`);
      }
      if (wind) pieces.push(`${wind}.`);
      if (humidity) pieces.push(`${humidity}.`);

      if (pieces.length > 0) return pieces.join(' ');
      // Fallback to original string if parsing failed
      return src;
    } catch {
      return notes;
    }
  };
  
  // Find earliest crew call time (as backup)
  const earliestCrewCall = callsheet.crewCalls && callsheet.crewCalls.length > 0 
    ? callsheet.crewCalls.reduce((earliest, crew) => {
        if (!crew.callTime) return earliest;
        return !earliest || crew.callTime < earliest ? crew.callTime : earliest;
      }, null) 
    : 'Not set';

  // Group crew by department
  const crewByDepartment = {};
  if (callsheet.crewCalls && callsheet.crewCalls.length > 0) {
    callsheet.crewCalls.forEach(crew => {
      const dept = crew.department || 'Other';
      if (!crewByDepartment[dept]) {
        crewByDepartment[dept] = [];
      }
      crewByDepartment[dept].push(crew);
    });
  }
  // Preferred department order (fallback to alpha for unknown)
  const normalize = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const deptRank = {
    'production': 1,
    'sound': 2,
    'location': 3,
    'assistant-directors': 4,
    'ad': 4,
    'production-assistants': 5,
    'set-pa': 5,
    'hair': 6,
    'makeup': 7,
    'costume': 8,
    'camera': 9,
    'electric': 10,
    'grip': 11,
    'art': 12,
    'set-decorating': 13,
    'property': 14,
    'post-production': 15,
    'vfx': 16,
    'special-effects': 17,
    'publicity': 18,
    'accounting': 19,
    'medical': 20,
    'stunts': 21,
    'transportation': 22,
    'craft-services': 23,
    'catering': 24,
    'casting': 25,
    'animals': 26,
    'vendors': 27,
    'additional-labor': 28,
    'other': 99
  };
  const orderedDepartments = Object.keys(crewByDepartment).sort((a, b) => {
    const na = normalize(a);
    const nb = normalize(b);
    const ra = deptRank[na] ?? 50;
    const rb = deptRank[nb] ?? 50;
    if (ra !== rb) return ra - rb;
    return a.localeCompare(b);
  });
  
  // Generate HTML content for printing
  const printContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Call Sheet - ${callsheet.projectTitle} - ${formattedDate}</title>
      <style>
        @page {
          size: ${paperSize} portrait;
          margin: ${margins};
        }
        body {
          font-family: Arial, sans-serif;
          margin: 0;
          padding: 0;
          color: #000;
          font-size: 9pt;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .page {
          width: 8.5in;
          min-height: ${paperSize === 'legal' ? '14in' : '11in'};
          padding: 0.25in;
          position: relative;
          box-sizing: border-box;
        }
        .page-title {
          text-align: center;
          font-weight: 800;
          font-size: ${paperSize === 'letter' ? '18pt' : '20pt'};
          text-transform: uppercase;
          margin-bottom: 0.08in;
        }
        
        /* Main Header Styles */
        .main-header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 0.1in;
        }
        .project-info {
          width: 30%;
          text-align: left;
          font-size: 8pt;
          padding-right: 8px; /* gutter away from crew-call border */
        }
        .project-info .production-header {
          text-align: center;
          margin-bottom: 8px; /* extra space before Director line */
        }
        .project-info .production-logo {
          height: 52px; /* bigger logo */
          max-width: 240px;
          object-fit: contain;
          display: block;
          margin: 0 auto 8px; /* more space below logo */
        }
        .project-info .production-office {
          margin-top: 6px;
        }
        .crew-call-header {
          width: 40%;
          text-align: center;
          border: 2px solid #000;
          padding: 5px;
        }
        .crew-call-header-title {
          font-weight: bold;
          text-transform: uppercase;
          font-size: 12pt;
          margin: 0;
        }
        .crew-call-time {
          font-size: 24pt;
          font-weight: bold;
          margin: 5px 0;
        }
        .crew-call-safety-line {
          font-size: 8pt;
          margin-top: 2px;
          padding-top: 3px;
          border-top: 1px dashed #999;
          text-align: left;
          max-height: 30px;
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }
        .date-info {
          width: 30%;
          text-align: right;
          font-size: 8pt;
          padding-left: 8px; /* gutter away from crew-call border */
        }
        .weather-card {
          display: inline-block;
          width: 100%;
          text-align: right;
          border: 1px solid #ccc;
          background: #fff;
            
          border-radius: 6px;
          padding: 6px;
          box-sizing: border-box;
        }
        .weather-card-top {
          display: flex;
          gap: 8px;
          align-items: center;
          justify-content: flex-end;
        }
        .weather-icon-lg {
          font-size: 20pt;
          line-height: 1;
        }
        .weather-temp-lg {
          font-size: 16pt;
          font-weight: 700;
          margin: 0;
        }
        .weather-cond {
          font-size: 9pt;
          margin: 0;
        }
        .weather-metrics-mini {
          display: flex;
          gap: 10px;
          justify-content: flex-end;
          margin-top: 4px;
          font-size: 8pt;
        }
        
        /* Safety Box */
        .safety-box {
          position: absolute;
          top: 0.25in;
          right: 0.25in;
          width: 60px;
          height: 60px;
          border: 2px solid #000;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          font-size: 7pt;
          text-align: center;
          background-color: #fff;
        }
        .safety-icon {
          font-size: 24pt;
          color: #f90;
          margin-bottom: 2px;
        }
        
        /* Production Info Bar */
        .production-info-bar {
          display: flex;
          justify-content: space-between;
          margin: 0.1in 0;
          border: 1px solid #000;
          background-color: #f0f0f0;
        }
        .production-info-item {
          flex: 1;
          padding: 5px;
          text-align: center;
          font-size: 8pt;
          border-right: 1px solid #000;
        }
        .production-info-item:last-child {
          border-right: none;
        }
        
        /* Location Boxes */
        .location-boxes {
          display: flex;
          justify-content: space-between;
          margin: 0.1in 0;
        }
        .location-box {
          width: 32%;
          border: 1px solid #000;
          padding: 5px;
          font-size: 8pt;
        }
        .location-title {
          font-weight: bold;
          text-transform: uppercase;
          margin-bottom: 3px;
        }
        
        /* Schedule Table */
        .schedule-table {
          width: 100%;
          border-collapse: collapse;
          margin: 0.1in 0;
          font-size: 8pt;
        }
        .schedule-table th, .schedule-table td {
          border: 1px solid #999;
          padding: 3px 4px;
        }
        .schedule-table th {
          background-color: #e0e0e0;
          font-weight: bold;
          text-align: center;
        }
        .schedule-table td {
          vertical-align: top;
        }
        .schedule-table .scene-number {
          text-align: center;
          width: 30px;
        }
        /* Ensure scene number cell is centered vertically as well */
        .schedule-table td.scene-number {
          vertical-align: middle;
        }
        .schedule-table .set-description {
          text-align: left;
        }
        .schedule-table .day-night {
          text-align: center;
          width: 30px;
        }
        .schedule-table .pages {
          text-align: center;
          width: 30px;
        }
        .schedule-table .cast {
          text-align: center;
          width: 50px;
        }
        
        /* Talent Calls Table */
        .talent-table {
          width: 100%;
          border-collapse: collapse;
          margin: 0.1in 0;
          font-size: 8pt;
        }
        .talent-table th, .talent-table td {
          border: 1px solid #000;
          padding: 4px;
          text-align: center;
        }
        .talent-table th {
          background-color: #e0e0e0;
          font-weight: bold;
        }
        .talent-table .number-column {
          width: 20px;
        }
        .talent-table .name-column {
          text-align: left;
        }
        .talent-table .role-column {
          text-align: left;
        }
        .talent-table .status-column {
          width: 30px;
        }
        .talent-table .time-column {
          width: 60px;
          white-space: nowrap;
        }
        
        /* Crew Calls Table */
        .crew-calls-page {
          font-size: 8pt;
        }
        .crew-calls-header {
          text-align: center;
          font-size: 18pt;
          font-weight: bold;
          margin-bottom: 0.1in;
          border-bottom: 2px solid #000;
        }
        .crew-department {
          margin: 0.1in 0;
        }
        .department-title {
          font-weight: bold;
          background-color: #e0e0e0;
          padding: 4px;
          border: 1px solid #000;
          text-transform: uppercase;
        }
        .crew-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 8pt;
        }
        .crew-table td {
          border: 1px solid #000;
          padding: 4px;
        }
        .crew-table .role-column {
          width: 25%;
        }
        .crew-table .name-column {
          width: 25%;
        }
        .crew-table .call-column {
          width: 10%;
          text-align: center;
        }
        
        /* Footer */
        .footer {
          position: absolute;
          bottom: 0.25in;
          left: 0.25in;
          right: 0.25in;
          text-align: center;
          font-size: 7pt;
          border-top: 1px solid #000;
          padding-top: 5px;
        }
        
        /* Additional Sections */
        .section-title {
          font-weight: bold;
          text-transform: uppercase;
          margin: 0.1in 0 0.05in 0;
          border-bottom: 1px solid #000;
          font-size: 10pt;
        }
        .notes-box {
          border: 1px solid #000;
          padding: 5px;
          margin: 0.05in 0;
          min-height: 50px;
        }
        .hospital-box {
          border: 1px solid #000;
          padding: 5px;
          margin: 0.1in 0;
          text-align: center;
        }
        .quote-box {
          border: 1px solid #000;
          padding: 5px;
          margin: 0.1in 0;
          text-align: center;
          font-style: italic;
        }
        
        /* Page Break */
        .page-break {
          page-break-before: always;
        }
        .key-info {
          display: flex;
          justify-content: space-between;
          margin-bottom: 0.1in;
          border: 1px solid #000;
        }
        .key-info-column {
          flex: 1;
          padding: 5px;
        }
        .key-info-column:not(:last-child) {
          border-right: 1px solid #000;
        }
        .key-info-title {
          font-weight: bold;
          text-align: center;
          background-color: #f0f0f0;
          padding: 3px 0;
          border-bottom: 1px solid #000;
        }
        .key-info-content {
          padding: 5px;
        }
        .section {
          margin-bottom: ${paperSize === 'letter' ? '0.15in' : '0.25in'};
        }
        .advance-schedule-section {
          margin-top: 0.1in;
          margin-bottom: 0.15in;
          border: 1px solid #336699;
          border-radius: 4px;
          padding: 0.1in;
          background-color: #f5f8fa;
        }
        .advance-schedule-section .section-title {
          color: #336699;
          border-bottom: 1px solid #336699;
          margin-top: 0;
        }
        .advance-schedule-table {
          margin-bottom: 0;
        }
        .section-title {
          font-size: ${paperSize === 'letter' ? '12pt' : '13pt'};
          font-weight: bold;
          margin-bottom: ${paperSize === 'letter' ? '0.1in' : '0.15in'};
          border-bottom: 1px solid #000;
          text-transform: uppercase;
        }
        .section-title.small {
          font-size: ${paperSize === 'letter' ? '9pt' : '10pt'};
          margin-bottom: ${paperSize === 'letter' ? '0.05in' : '0.08in'};
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: ${paperSize === 'letter' ? '0.05in' : '0.1in'};
          font-size: ${paperSize === 'letter' ? '8.5pt' : '10pt'};
        }
        th, td {
          border: 1px solid #999;
          padding: ${paperSize === 'letter' ? '2px 3px' : '3px 4px'};
          text-align: left;
          font-size: ${paperSize === 'letter' ? '8pt' : '9pt'};
        }
        th {
          background-color: #f5f5f5;
          font-weight: bold;
        }
        .schedule-table th, .schedule-table td {
          text-align: center;
        }
        /* SCENE column (1st) - centered by default */
        /* I/E column (2nd) - centered by default */
        /* DESCRIPTION column (3rd) - left justified */
        .schedule-table th:nth-child(3), .schedule-table td:nth-child(3) {
          text-align: left;
        }
        /* CAST column (4th) - centered by default */
        /* D/N column (5th) - centered by default */
        /* PAGES column (6th) - centered by default */
        /* LOCATION ADDRESS column (7th) - left justified */
        .schedule-table th:nth-child(7), .schedule-table td:nth-child(7) {
          text-align: left;
        }
        /* Background table: left-align description in column 2 */
        .background-table th:nth-child(2), .background-table td:nth-child(2) {
          text-align: left;
        }
        /* Special Instructions table: left-align Department (column 1) */
        .special-table th:nth-child(1), .special-table td:nth-child(1) {
          text-align: left;
          width: 25%;
        }
        /* Special Instructions table: left-align Instructions (column 2) */
        .special-table th:nth-child(2), .special-table td:nth-child(2) {
          text-align: left;
          width: 60%;
        }
        .notes {
          border: 1px solid #999;
          padding: 5px;
        }
        /* Notes columns */
        .notes-columns {
          column-count: ${paperSize === 'legal' ? 3 : 2};
          column-gap: 0.25in;
          width: 100%;
        }
        .notes-block {
          break-inside: avoid;
          page-break-inside: avoid;
          margin-bottom: 0.15in;
          display: inline-block;
          width: 100%;
        }
        /* Two-column helper */
        .two-col {
          display: flex;
          gap: 0;
          align-items: flex-start;
        }
        .two-col .col {
          flex: 1;
          min-width: 0;
        }
        /* Print helpers */
        .print-avoid-break { page-break-inside: avoid; break-inside: avoid; }
        .clearfix { clear: both; }
        .page-break {
          page-break-before: always;
        }
        .no-print {
          display: none;
        }
        @media print {
          /* Common print settings for both paper sizes */
          .page-break { page-break-before: always; }
          .section { page-break-inside: avoid; }
          .schedule-table { page-break-inside: avoid; }
          table { page-break-inside: avoid; }
          /* Allow the Advance Schedule to split across pages so it can start at the bottom of page 1 */
          .advance-schedule-section { page-break-inside: auto; break-inside: auto; }
          .advance-schedule-table { page-break-inside: auto !important; break-inside: auto !important; }
          .advance-schedule-table tr { page-break-inside: avoid; }
          
          /* Advance schedule may split to allow it to begin at page 1 bottom */
          
          /* Specific optimizations based on paper size */
          ${paperSize === 'letter' ? `
          /* Letter size optimizations */
          .page { height: 11in; }
          ` : `
          /* Legal size optimizations */
          .page { height: 14in; }
          `}
        }
        .crew-call-table {
          font-size: ${paperSize === 'letter' ? '8pt' : '9pt'};
        }
        .crew-call-table th {
          background-color: #e0e0e0;
        }
        .crew-call-section {
          margin-bottom: 10px;
        }
        .crew-call-section-title {
          font-weight: bold;
          background-color: #f0f0f0;
          padding: ${paperSize === 'letter' ? '2px 4px' : '3px 5px'};
          border: 1px solid #000;
          text-transform: uppercase;
          font-size: ${paperSize === 'letter' ? '9pt' : '10pt'};
        }
        /* Department grid on Page 2 */
        .dept-grid {
          display: grid;
          grid-template-columns: ${paperSize === 'legal' ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)'};
          gap: 0; /* flush columns */
        }
        .dept-col { break-inside: avoid; page-break-inside: avoid; }
        .dept-title {
          font-weight: bold;
          background-color: #f0f0f0;
          padding: ${paperSize === 'letter' ? '2px 4px' : '3px 5px'};
          border: 1px solid #000;
          text-transform: uppercase;
          margin-bottom: 4px;
          font-size: ${paperSize === 'letter' ? '9pt' : '10pt'};
        }
        .dept-table { width: 100%; border-collapse: collapse; }
        .dept-table th, .dept-table td { border: 1px solid #000; border-color: #000; padding: ${paperSize === 'letter' ? '2px 3px' : '3px 4px'}; font-size: ${paperSize === 'letter' ? '8pt' : '9pt'}; }
        .dept-table th { background-color: #eaeaea; }
        /* Avoid double border between adjacent columns */
        /* Legal (3 columns): remove left border on columns 2 and 3 in each row */
        .dept-grid > .dept-col:nth-child(3n+2) .dept-title,
        .dept-grid > .dept-col:nth-child(3n+3) .dept-title,
        .dept-grid > .dept-col:nth-child(3n+2) .dept-table th,
        .dept-grid > .dept-col:nth-child(3n+2) .dept-table td,
        .dept-grid > .dept-col:nth-child(3n+3) .dept-table th,
        .dept-grid > .dept-col:nth-child(3n+3) .dept-table td {
          ${paperSize === 'legal' ? 'border-left: none;' : ''}
        }
        /* Letter (2 columns): remove left border on column 2 in each row */
        .dept-grid > .dept-col:nth-child(2n+2) .dept-title,
        .dept-grid > .dept-col:nth-child(2n+2) .dept-table th,
        .dept-grid > .dept-col:nth-child(2n+2) .dept-table td {
          ${paperSize === 'letter' ? 'border-left: none;' : ''}
        }
        .meal-table {
          width: 60%;
          border-collapse: collapse;
          font-size: ${paperSize === 'letter' ? '8pt' : '9pt'};
          margin-top: 6px;
        }
        .meal-table th, .meal-table td {
          border: 1px solid #000;
          padding: ${paperSize === 'letter' ? '2px 3px' : '3px 4px'};
          text-align: left;
        }
        /* Back page universal table border normalization */
        .page.page-break table { border-collapse: collapse; }
        .page.page-break table th,
        .page.page-break table td { border: 1px solid #000; border-color: #000; }
        .meal-table th { background-color: #e0e0e0; }
        .footer {
          position: absolute;
          bottom: ${paperSize === 'letter' ? '0.15in' : '0.25in'};
          left: ${paperSize === 'letter' ? '0.15in' : '0.25in'};
          right: ${paperSize === 'letter' ? '0.15in' : '0.25in'};
          text-align: center;
          font-size: ${paperSize === 'letter' ? '7pt' : '8pt'};
          border-top: 1px solid #000;
          padding-top: ${paperSize === 'letter' ? '3px' : '5px'};
        }
        .weather-box {
          padding: 10px;
          margin-bottom: 10px;
          position: relative;
          text-align: center;
        }
        .sunrise-sunset {
          display: flex;
          justify-content: space-between;
          margin-top: 8px;
          padding-top: 8px;
          border-top: 1px dashed #aaa;
          position: relative;
        }
        .sun-path {
          position: absolute;
          top: 0;
          left: 10%;
          right: 10%;
          height: 30px;
          border-top: 1px dashed #ffa500;
          border-radius: 50%;
          z-index: 0;
        }
        .weather-icon {
          font-size: 36px;
          margin-bottom: 5px;
          display: block;
        }
        .weather-temp {
          font-size: 18px;
          font-weight: bold;
          margin-bottom: 5px;
        }
        .weather-conditions {
          font-size: 12px;
          margin-bottom: 8px;
        }
        .weather-metrics {
          display: flex;
          justify-content: center;
          gap: 15px;
          margin: 5px 0;
          font-size: 11px;
        }
        .weather-metric-item {
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .metric-icon {
          font-size: 16px;
          margin-bottom: 2px;
        }
        .day-night-indicator {
          display: flex;
          justify-content: space-between;
          position: relative;
          margin: 15px 0 5px;
        }
        .day-night-bar {
          position: absolute;
          top: 50%;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(to right, #ffd700, #87ceeb, #000080);
          z-index: 0;
        }
        .time-marker {
          position: relative;
          z-index: 1;
          background-color: white;
          border-radius: 50%;
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid #000;
        }
      </style>
    </head>
    <body>
      <!-- FRONT PAGE -->
      <div class="page">
        <!-- Page Title -->
        <div class="page-title">${(callsheet.projectTitle || 'Untitled Project')}</div>

        <!-- Main Header with Project Info and Crew Call -->
        <div class="main-header">
          <div class="project-info">
            <div class="production-header">
              ${callsheet.productionLogo
                ? `<img class=\"production-logo\" src=\"${callsheet.productionLogo}\" alt=\"Production Logo\" />`
                : `<strong>${(callsheet.company || 'PRODUCTION MANAGEMENT')}</strong>`}
            </div>
            <div>Director: ${callsheet.director || 'Not specified'}</div>
            <div>Producer: ${callsheet.producer || 'Not specified'}</div>
            ${Array.isArray(callsheet.producers) && callsheet.producers.length > 0
              ? callsheet.producers.map(p => `<div>${(p.type || 'Producer')}: ${p.name || ''}</div>`).join('')
              : ''}
            <div>DOP: ${callsheet.dop || 'Not specified'}</div>
            <div class="production-office">Production Office: ${productionOffice || 'Not specified'}</div>
          </div>
          
          <div class="crew-call-header">
            <div class="crew-call-header-title">GENERAL CREW CALL</div>
            <div class="crew-call-time">${generalCallTime}</div>
          </div>
          
          <div class="date-info">
            <div><strong>${formattedDate}</strong></div>
            <div><strong>DAY ${callsheet.shootingDay || '1'} of ${callsheet.totalShootingDays || '1'}</strong></div>
            ${(() => {
              const sc = callsheet?.scheduleInfo?.shootingCall;
              const lt = callsheet?.scheduleInfo?.lunchTime;
              const scStr = sc ? formatAnyTime(sc) : '';
              const ltStr = lt ? formatAnyTime(lt) : '';
              if (!scStr && !ltStr) return '';
              return `
                <div style="margin-top: 4px; text-align: right;">
                  ${scStr ? `<div><strong>Shooting Call:</strong> ${scStr}</div>` : ''}
                  ${ltStr ? `<div><strong>Lunch:</strong> ${ltStr}</div>` : ''}
                </div>
              `;
            })()}
            <div style="margin-top: 5px;">
              <div class="weather-card">
                <div class="weather-card-top">
                  <div class="weather-icon-lg">${condIcon}</div>
                  <div>
                    <div class="weather-temp-lg">${wx.tempVal ? `${wx.tempVal}°${wx.tempUnit || 'F'}` : (callsheet.weatherNotes ? '' : '—')}</div>
                    <div class="weather-cond">${wx.condition || ''}</div>
                  </div>
                </div>
                <div class="weather-metrics-mini">
                  <div>${wx.windVal ? `Wind: ${wx.windVal} ${wx.windUnit || 'mph'}` : ''}</div>
                  <div>${wx.humidity ? `Humidity: ${wx.humidity}%` : ''}</div>
                </div>
              </div>
            </div>
            <div style="margin-top: 4px;">
              <strong>SUNRISE:</strong> ${sunriseStr} | <strong>SUNSET:</strong> ${sunsetStr}
            </div>
          </div>
        </div>

        

        <!-- Top Info Strip: Nearest Hospital | Crew Parking | Basecamp -->
        <div class="top-info-strip" style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px; margin: 8px 0 12px 0;">
          <div class="info-box" style="border: 1px solid #ccc; padding: 8px; border-radius: 6px; background: #fff;">
            <div style="font-weight: 700; margin-bottom: 4px;">Nearest Hospital</div>
            <div>${nearestHospital || 'Not specified'}</div>
          </div>
          <div class="info-box" style="border: 1px solid #ccc; padding: 8px; border-radius: 6px; background: #fff;">
            <div style="font-weight: 700; margin-bottom: 4px;">Crew Parking</div>
            <div>${crewParking || 'See details below'}</div>
          </div>
          <div class="info-box" style="border: 1px solid #ccc; padding: 8px; border-radius: 6px; background: #fff;">
            <div style="font-weight: 700; margin-bottom: 4px;">Basecamp</div>
            <div>${basecamp || 'See details below'}</div>
          </div>
        </div>

        <!-- Production Info Bar (optional) -->
        ${hasParkingSummary ? `
        <div class="production-info-bar">
          <div class="production-info-item">
            <strong>Parking</strong><br>
            ${callsheet.parkingNotes.substring(0, 50)}${callsheet.parkingNotes.length > 50 ? '...' : ''}
          </div>
        </div>
        ` : ''}
        
        <!-- Location Boxes -->
        <div class="location-boxes">
          ${callsheet.locations && callsheet.locations.length > 0 ? callsheet.locations.filter(location => location.name && location.name.trim()).map((location, index) => `
            <div class="location-box">
              <div class="location-title">${location.name}</div>
              <div>${location.address || ''}</div>
              ${location.notes ? `<div>${location.notes}</div>` : ''}
            </div>
          `).join('') : ''}
        </div>
        
        
        
        <!-- Schedule Section -->
        ${safetyLine ? `<div class="crew-call-safety-line" style="margin: 6px 0 4px 0;">` +
          `<strong>SAFETY:</strong> ${safetyLine}` +
        `</div>` : ''}
        ${showMissingScheduleMessage && (!Array.isArray(callsheet.schedule) || callsheet.schedule.length === 0) ? `
          <div style="border: 2px dashed #f44336; padding: 15px; margin-bottom: 15px; background-color: #ffebee; text-align: center;">
            <p style="font-weight: bold; color: #d32f2f; margin: 0 0 10px 0;">⚠️ Schedule Data Not Loaded</p>
            <p style="margin: 0;">To view the schedule in your callsheet, please first click on the Schedule tab before printing.</p>
          </div>
        ` : ''}
        <table class="schedule-table">
          <tr>
            <th>SCENE</th>
            <th>I/E</th>
            <th>DESCRIPTION</th>
            <th>D/N</th>
            <th>CAST</th>
            <th>PAGES</th>
            <th>LOCATION ADDRESS</th>
          </tr>
          ${Array.isArray(callsheet.schedule) && callsheet.schedule.length > 0 ? callsheet.schedule.map((item, index) => `
            <tr>
              <td class="scene-number">${item.sceneNumber || ''}</td>
              <td class="int-ext">${item.location_type || (item.setScene && item.setScene.trim().toUpperCase().includes('INT.') ? 'INT.' : item.setScene && item.setScene.trim().toUpperCase().includes('EXT.') ? 'EXT.' : '')}</td>
              <td class="description">${(() => { const d = item.description || ''; const s = String(d); const m = s.match(/[^.!?\n]*[\.!?]/); return (m ? m[0] : s.split('\n')[0] || '').trim(); })()}</td>
              <td class="day-night">${item.dayNight || 'D'}</td>
              <td class="cast">${(() => {
                const c = item.cast;
                if (!c || (Array.isArray(c) && c.length === 0)) return '';
                const arr = Array.isArray(c) ? c : String(c).split(',');
                const tc = Array.isArray(callsheet.talentCalls) ? callsheet.talentCalls : [];
                const norm = s => String(s || '').trim().toLowerCase();
                const numbers = [];
                arr.forEach(raw => {
                  const s = String(raw).trim();
                  // 1) direct digits in entry
                  const m = s.match(/\d+/g);
                  if (m && m.length) {
                    numbers.push(m.join(''));
                    return;
                  }
                  // 2) try to match character or cast name from talentCalls
                  const target = norm(s);
                  const hit = tc.find(t => (
                    norm(t?.character) === target ||
                    norm(t?.name) === target
                  ));
                  if (hit && (hit.castNumber || hit.castNumber === 0)) {
                    numbers.push(String(hit.castNumber));
                  }
                });
                const uniq = Array.from(new Set(numbers)).filter(Boolean);
                return uniq.length ? uniq.join(', ') : '';
              })()}</td>
              <td class="pages">${formatPagesEighthsOnly(item.pages)}</td>
              <td class="location-address">${item.locationAddress || ''}</td>
            </tr>
          `).join('') : `
            <tr>
              <td colspan="7" style="text-align: center; padding: 20px;">${showMissingScheduleMessage ? 'Click on the Schedule tab to load schedule data.' : 'No scenes scheduled for this day.'}</td>
            </tr>
          `}
          
        </table>
        
        <!-- Talent Calls Section -->
        
        <table class="talent-table">
          <tr>
            <th class="number-column">#</th>
            <th class="role-column">CHARACTER</th>
            <th class="name-column">CAST</th>
            <th class="status-column">STATUS</th>
            <th class="time-column">PICKUP</th>
            <th class="time-column">CALL</th>
            <th class="time-column">SET</th>
            <th>NOTES</th>
          </tr>
          ${Array.isArray(callsheet.talentCalls) ? callsheet.talentCalls.map((talent, index) => {
            // Format times for display
            const formatTime = (timeString) => {
              if (!timeString) return '';
              try {
                const [hours, minutes] = timeString.split(':').map(Number);
                const date = new Date();
                date.setHours(hours);
                date.setMinutes(minutes);
                const t = date.toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                }).toUpperCase();
                // Ensure AM/PM stays on same line
                return t.replace(' AM', '&nbsp;AM').replace(' PM', '&nbsp;PM');
              } catch (e) {
                return timeString;
              }
            };
            
            // Determine SET: prefer explicit setCallTime, fallback to callTime
            const getSetTime = (talent) => {
              if (talent.setCallTime) return formatTime(talent.setCallTime);
              return formatTime(talent.callTime);
            };
            
            return `
              <tr>
                <td>${talent.castNumber || ''}</td>
                <td class="role-column">${talent.character || ''}</td>
                <td class="name-column">${talent.name || ''}</td>
                <td>${talent.status || 'SW'}</td>
                <td>${formatTime(talent.pickupTime)}</td>
                <td>${formatTime(talent.callTime)}</td>
                <td>${getSetTime(talent)}</td>
                <td>${talent.notes || ''}</td>
              </tr>
            `;
          }).join('') : `
            <tr>
              <td colspan="9" style="text-align: center; padding: 20px;">No talent scheduled for this day.</td>
            </tr>
          `}
        </table>
        
        <!-- Background and Special Instructions two-column layout -->
        ${(() => {
          // Build Background HTML if any
          const hasBackground = Array.isArray(callsheet.background) && callsheet.background.length > 0;
          const backgroundHTML = hasBackground ? `
            <div class="col">
              <table class="schedule-table background-table">
                <tr>
                  <th>QUANTITY</th>
                  <th>BACKGROUND</th>
                  <th>TIME</th>
                </tr>
                ${callsheet.background.map(bg => `
                  <tr>
                    <td>${bg.qty || ''}</td>
                    <td>${(bg.description || bg.notes || '').toString()}</td>
                    <td>${bg.call || ''}</td>
                  </tr>
                `).join('')}
              </table>
            </div>
          ` : '';

          // Build Special Instructions rows
          const rows = [];
          const sections = [
            { dept: 'Camera', key: 'cameraNotes' },
            { dept: 'Lighting', key: 'lightingNotes' },
            { dept: 'Props', key: 'propsNotes' },
            { dept: 'Sound', key: 'soundNotes' },
            { dept: 'VFX', key: 'vfxNotes' },
            { dept: 'Special FX', key: 'specialFxNotes' },
            { dept: 'Vehicles', key: 'vehiclesNotes' },
            { dept: 'Stunts', key: 'stuntsNotes' },
            { dept: 'Animals', key: 'animalsNotes' },
            { dept: 'Hair/Makeup', key: 'hairMakeupNotes' },
            { dept: 'Misc', key: 'miscNotes' }
          ];
          sections.forEach(({ dept, key }) => {
            const txt = (callsheet && callsheet[key]) ? String(callsheet[key]).trim() : '';
            if (txt) {
              rows.push(`<tr><td style="font-weight: 600;">${dept}</td><td>${txt.replace(/\n/g, '<br>')}</td></tr>`);
            }
          });
          const hasSpecial = rows.length > 0;
          const specialHTML = hasSpecial ? `
            <div class="col">
              <table class="schedule-table special-table">
                <tr>
                  <th>DEPARTMENT</th>
                  <th>SPECIAL INSTRUCTIONS</th>
                </tr>
                ${rows.join('')}
              </table>
            </div>
          ` : '';

          if (!hasBackground && !hasSpecial) return '';
          if (hasBackground && hasSpecial) {
            return `<div class="two-col">${backgroundHTML}${specialHTML}</div>`;
          }
          // Only one present; return it without two-col wrapper
          return backgroundHTML || specialHTML;
        })()}
        
        
        
        <!-- START ADVANCE SCHEDULE SECTION -->
        <div class="advance-schedule-section">
          <div class="section-title">ADVANCE SCHEDULE - DAY ${callsheet.shootingDay ? parseInt(callsheet.shootingDay) + 1 : '2'}</div>
          ${showMissingScheduleMessage && (!Array.isArray(callsheet.nextDaySchedule) || callsheet.nextDaySchedule.length === 0) ? `
            <div style="border: 2px dashed #f44336; padding: 15px; margin-bottom: 15px; background-color: #ffebee; text-align: center;">
              <p style="font-weight: bold; color: #d32f2f; margin: 0 0 10px 0;">⚠️ Advance Schedule Data Not Loaded</p>
              <p style="margin: 0;">To view the advance schedule in your callsheet, please first click on the Schedule tab before printing.</p>
            </div>
          ` : ''}
          <table class="schedule-table advance-schedule-table">
          <tr>
            <th>SCENE</th>
            <th>I/E</th>
            <th>DESCRIPTION</th>
            <th>D/N</th>
            <th>CAST</th>
            <th>PAGES</th>
            <th>LOCATION ADDRESS</th>
          </tr>
          ${Array.isArray(callsheet.nextDaySchedule) && callsheet.nextDaySchedule.length > 0 ? callsheet.nextDaySchedule.map((item, index) => `
            <tr>
              <td class="scene-number">${item.sceneNumber || ''}</td>
              <td class="int-ext">${item.location_type || (item.setScene && item.setScene.trim().toUpperCase().includes('INT.') ? 'INT.' : item.setScene && item.setScene.trim().toUpperCase().includes('EXT.') ? 'EXT.' : '')}</td>
              <td class="description">${(() => { const d = item.description || ''; const s = String(d); const m = s.match(/[^.!?\n]*[\.!?]/); return (m ? m[0] : s.split('\n')[0] || '').trim(); })()}</td>
              <td class="day-night">${item.dayNight || 'D'}</td>
              <td class="cast">${(() => {
                const c = item.cast;
                if (!c || (Array.isArray(c) && c.length === 0)) return '';
                const arr = Array.isArray(c) ? c : String(c).split(',');
                const tc = Array.isArray(callsheet.talentCalls) ? callsheet.talentCalls : [];
                const norm = s => String(s || '').trim().toLowerCase();
                const numbers = [];
                arr.forEach(raw => {
                  const s = String(raw).trim();
                  const m = s.match(/\d+/g);
                  if (m && m.length) { numbers.push(m.join('')); return; }
                  const target = norm(s);
                  const hit = tc.find(t => (norm(t?.character) === target || norm(t?.name) === target));
                  if (hit && (hit.castNumber || hit.castNumber === 0)) numbers.push(String(hit.castNumber));
                });
                const uniq = Array.from(new Set(numbers)).filter(Boolean);
                return uniq.length ? uniq.join(', ') : '';
              })()}</td>
              <td class="pages">${formatPagesEighthsOnly(item.pages)}</td>
              <td class="location-address">${item.locationAddress || ''}</td>
            </tr>
          `).join('') : `
            <tr>
              <td colspan="7" style="text-align: center; padding: 20px;">${showMissingScheduleMessage ? 'Click on the Schedule tab to load advance schedule data.' : 'No scenes scheduled for the next day.'}</td>
            </tr>
          `}
          
          </table>
        </div>
        
        <!-- Footer -->
        <div class="footer">
          ${callsheet.projectTitle || 'Untitled Project'} - Call Sheet #${callsheet.shootingDay || '1'} - ${formattedDate}
        </div>
        <!-- END ADVANCE SCHEDULE SECTION -->
      </div>
      
      <!-- BACK PAGE (CREW CALLS) -->
      <div class="page page-break">
        <div class="header">
          <div class="header-left">
            <div class="project-title">${callsheet.projectTitle || 'Untitled Project'}</div>
          </div>
          <div class="header-center">
            <div class="callsheet-number">CREW CALLS</div>
          </div>
          <div class="header-right">
            <div><strong>DATE:</strong> ${formattedDate}</div>
            <div><strong>Day ${callsheet.shootingDay || '1'}</strong></div>
          </div>
        </div>
        
        <!-- Crew calls by department (columns), rows are positions/roles -->
        <div class="dept-grid">
          ${orderedDepartments.map(dept => `
            <div class="dept-col">
              <div class="dept-title">${dept}
                ${(() => {
                  try {
                    const meta = (callsheet && callsheet.departmentMeta && callsheet.departmentMeta[dept]) || null;
                    const walkie = (callsheet && callsheet.walkieChannels && callsheet.walkieChannels[dept]) || (meta && meta.walkie);
                    return walkie ? `<div style=\"font-weight:normal; font-size:${paperSize === 'letter' ? '8pt' : '9pt'}; margin-top:2px; text-transform:none;\">Walkie Channel ${walkie}</div>` : '';
                  } catch { return ''; }
                })()}
              </div>
              <table class="dept-table">
                <tr>
                  <th>Title</th>
                  <th>Name</th>
                  <th>Call</th>
                </tr>
                ${crewByDepartment[dept].map((c) => `
                  <tr>
                    <td>${c.role || ''}</td>
                    <td>${c.name || ''}</td>
                    <td style="text-align:center; white-space:nowrap;">${c.callTime ? formatTimeDisplay(c.callTime) : ''}</td>
                  </tr>
                `).join('')}
              </table>
            </div>
          `).join('')}
        </div>

        ${(() => {
          const meals = callsheet && callsheet.meals ? callsheet.meals : null;
          if (!meals) return '';
          const fmt = (t) => t ? formatTimeDisplay(t) : '';
          const rows = [];
          if (meals.courtesyBreakfast || meals.courtesyBreakfastTime) {
            rows.push(`<tr><td>Courtesy Breakfast</td><td>${meals.courtesyBreakfast || ''}</td><td>${fmt(meals.courtesyBreakfastTime)}</td></tr>`);
          }
          if (meals.crewLunch || meals.crewLunchTime) {
            rows.push(`<tr><td>Crew Lunch</td><td>${meals.crewLunch || ''}</td><td>${fmt(meals.crewLunchTime)}</td></tr>`);
          }
          if (meals.backgroundLunch || meals.backgroundLunchTime) {
            rows.push(`<tr><td>Background Lunch</td><td>${meals.backgroundLunch || ''}</td><td>${fmt(meals.backgroundLunchTime)}</td></tr>`);
          }
          if (rows.length === 0) return '';
          return `
            <div class="crew-call-section">
              <div class="crew-call-section-title">CATERING / MEALS</div>
              <table class="meal-table">
                <tr>
                  <th>Meal</th>
                  <th>Count</th>
                  <th>Time</th>
                </tr>
                ${rows.join('')}
              </table>
            </div>
          `;
        })()}
        
        <!-- Additional Locations -->
        ${callsheet.additionalLocations && callsheet.additionalLocations.length > 0 ? `
          <div class="section">
            <div class="section-title">ADDITIONAL LOCATIONS</div>
            <table>
              <tr>
                <th>Location</th>
                <th>Address</th>
              </tr>
              ${callsheet.additionalLocations.map(loc => `
                <tr>
                  <td>${loc.name || 'Unnamed'}</td>
                  <td>${loc.address || 'No address'}</td>
                </tr>
              `).join('')}
            </table>
          </div>
        ` : ''}
        <!-- Emergency Contacts (Compact) -->
        <div class="section print-avoid-break">
          <div class="section-title">EMERGENCY CONTACTS</div>
          <table style="width:100%; border-collapse:collapse;">
            <tr>
              <th style="width:40%;">Contact</th>
              <th>Details</th>
            </tr>
            ${(() => {
              const rows = [];
              const emergency = callsheet?.emergencyContacts;
              const emergencyNumber = emergency?.police || emergency?.fire || emergency?.ambulance || '911';
              rows.push(`<tr><td>Emergency (Police/Fire/Ambulance)</td><td>${emergencyNumber}</td></tr>`);
              if (emergency?.setMedic) rows.push(`<tr><td>Set Medic</td><td>${emergency.setMedic}</td></tr>`);
              if (emergency?.productionOffice) rows.push(`<tr><td>Production Office</td><td>${emergency.productionOffice}</td></tr>`);
              if (emergency?.locationManager) rows.push(`<tr><td>Location Manager</td><td>${emergency.locationManager}</td></tr>`);
              return rows.join('');
            })()}
          </table>
        </div>
        
        <div class="footer">
          ${callsheet.projectTitle || 'Untitled Project'} - Crew Calls - ${formattedDate}
        </div>
      </div>
      
      <div class="no-print" style="padding: 20px; text-align: center;">
        <button onclick="window.print()" style="padding: 10px 20px; font-size: 16px; cursor: pointer;">Print Call Sheet</button>
      </div>
    </body>
    </html>
  `;
  
  // Create an invisible iframe
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  document.body.appendChild(iframe);
  
  // Write content to the iframe
  iframe.contentDocument.open();
  iframe.contentDocument.write(printContent);
  iframe.contentDocument.close();
  
  // Print the iframe content when it's loaded
  iframe.onload = function() {
    setTimeout(function() {
      iframe.contentWindow.print();
      
      // Remove the iframe after printing (or after a delay)
      setTimeout(function() {
        document.body.removeChild(iframe);
      }, 1000);
    }, 500);
  };
};

/**
 * Get a weather icon based on the weather description
 * @param {string} weatherNotes - Weather notes text
 * @returns {string} - Weather icon emoji
 */
const getWeatherIcon = (weatherNotes) => {
  if (!weatherNotes || weatherNotes === 'Not specified') {
    return '🌤️';
  }
  
  const weatherText = weatherNotes.toLowerCase();
  
  if (weatherText.includes('clear') || weatherText.includes('sunny')) {
    return '☀️';
  } else if (weatherText.includes('partly cloudy') || weatherText.includes('mostly sunny')) {
    return '⛅';
  } else if (weatherText.includes('cloudy') || weatherText.includes('overcast')) {
    return '☁️';
  } else if (weatherText.includes('rain') || weatherText.includes('shower')) {
    return '🌧️';
  } else if (weatherText.includes('storm') || weatherText.includes('thunder')) {
    return '⛈️';
  } else if (weatherText.includes('snow') || weatherText.includes('flurries')) {
    return '❄️';
  } else if (weatherText.includes('fog') || weatherText.includes('mist')) {
    return '🌫️';
  } else if (weatherText.includes('wind')) {
    return '💨';
  } else {
    return '🌤️';
  }
};

/**
 * Extract temperature from weather notes
 * @param {string} weatherNotes - Weather notes text
 * @returns {string} - HTML for temperature display
 */
const getWeatherTemp = (weatherNotes) => {
  if (!weatherNotes || weatherNotes === 'Not specified') {
    return '<div class="weather-temp">--°F</div>';
  }
  
  const tempMatch = weatherNotes.match(/(\d+)°F/);
  if (tempMatch) {
    return `<div class="weather-temp">${tempMatch[0]}</div>`;
  }
  
  return '<div class="weather-temp">--°F</div>';
};

/**
 * Extract weather condition from notes
 * @param {string} weatherNotes - Weather notes text
 * @returns {string} - Weather condition text
 */
const getWeatherCondition = (weatherNotes) => {
  if (!weatherNotes || weatherNotes === 'Not specified') {
    return 'Weather information not available';
  }
  
  // Try to extract the weather description
  const conditions = [
    'clear', 'sunny', 'partly cloudy', 'mostly sunny', 'cloudy', 
    'overcast', 'rain', 'shower', 'storm', 'thunder', 'snow', 
    'flurries', 'fog', 'mist', 'wind'
  ];
  
  const weatherText = weatherNotes.toLowerCase();
  for (const condition of conditions) {
    if (weatherText.includes(condition)) {
      // Capitalize first letter of each word
      return condition.split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
  }
  
  // If no specific condition found, return a shortened version of the notes
  return weatherNotes.length > 30 ? weatherNotes.substring(0, 30) + '...' : weatherNotes;
};

/**
 * Extract precipitation chance from weather notes
 * @param {string} weatherNotes - Weather notes text
 * @returns {string} - Precipitation chance
 */
const getPrecipitation = (weatherNotes) => {
  if (!weatherNotes || weatherNotes === 'Not specified') {
    return '0%';
  }
  
  const precipMatch = weatherNotes.match(/precipitation:\s*(\d+)%/i) || 
                      weatherNotes.match(/precip[^:]*:\s*(\d+)%/i) ||
                      weatherNotes.match(/(\d+)%\s*chance/i);
  
  return precipMatch ? `${precipMatch[1]}%` : '0%';
};

/**
 * Extract wind speed from weather notes
 * @param {string} weatherNotes - Weather notes text
 * @returns {string} - Wind speed
 */
const getWindSpeed = (weatherNotes) => {
  if (!weatherNotes || weatherNotes === 'Not specified') {
    return '0 mph';
  }
  
  const windMatch = weatherNotes.match(/wind:\s*([\d.]+)\s*mph/i) ||
                    weatherNotes.match(/([\d.]+)\s*mph/i);
  
  return windMatch ? `${windMatch[1]} mph` : '0 mph';
};

/**
 * Extract or estimate humidity from weather notes
 * @param {string} weatherNotes - Weather notes text
 * @returns {string} - Humidity percentage
 */
const getHumidity = (weatherNotes) => {
  if (!weatherNotes || weatherNotes === 'Not specified') {
    return '50%';
  }
  
  const humidityMatch = weatherNotes.match(/humidity:\s*(\d+)%/i) ||
                         weatherNotes.match(/(\d+)%\s*humidity/i);
  
  if (humidityMatch) {
    return `${humidityMatch[1]}%`;
  }
  
  // Estimate humidity based on weather conditions
  const weatherText = weatherNotes.toLowerCase();
  if (weatherText.includes('rain') || weatherText.includes('shower') || weatherText.includes('storm')) {
    return '80%';
  } else if (weatherText.includes('fog') || weatherText.includes('mist')) {
    return '90%';
  } else if (weatherText.includes('snow')) {
    return '70%';
  } else if (weatherText.includes('clear') || weatherText.includes('sunny')) {
    return '40%';
  } else {
    return '50%';
  }
};

/**
 * Calculate approximate sunrise time based on date and season
 * @param {Date} date - The date to calculate sunrise for
 * @returns {string} - Formatted sunrise time
 */
const getSunriseTime = (date) => {
  if (!date) return '06:00 AM';
  
  const month = new Date(date).getMonth(); // 0-11
  
  // Approximate sunrise times by season (Northern Hemisphere)
  if (month >= 2 && month <= 4) { // Spring (Mar-May)
    return '06:30 AM';
  } else if (month >= 5 && month <= 7) { // Summer (Jun-Aug)
    return '05:45 AM';
  } else if (month >= 8 && month <= 10) { // Fall (Sep-Nov)
    return '07:00 AM';
  } else { // Winter (Dec-Feb)
    return '07:30 AM';
  }
};

/**
 * Calculate approximate sunset time based on date and season
 * @param {Date} date - The date to calculate sunset for
 * @returns {string} - Formatted sunset time
 */
const getSunsetTime = (date) => {
  if (!date) return '08:00 PM';
  
  const month = new Date(date).getMonth(); // 0-11
  
  // Approximate sunset times by season (Northern Hemisphere)
  if (month >= 2 && month <= 4) { // Spring (Mar-May)
    return '07:30 PM';
  } else if (month >= 5 && month <= 7) { // Summer (Jun-Aug)
    return '08:30 PM';
  } else if (month >= 8 && month <= 10) { // Fall (Sep-Nov)
    return '06:45 PM';
  } else { // Winter (Dec-Feb)
    return '05:30 PM';
  }
};

// Export the weather-related helper functions for use in other components
export {
  getWeatherIcon,
  getWeatherTemp,
  getWeatherCondition,
  getPrecipitation,
  getWindSpeed,
  getHumidity,
  getSunriseTime,
  getSunsetTime
};
